import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { LocationPickerComponent } from '../../../../../../../../../shared/components/location-picker/location-picker.component';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { currentLocation, projectDetails, siteDetails } from '../../../../../../../core/projects.selectors';
import { CreateSiteRequest, UpdateSiteRequest } from '../../../../../../../core/projects.actions';
import * as moment from 'moment';

@Component({
    selector: 'app-create-or-edit-site',
    templateUrl: './create-or-edit-site.component.html',
    styleUrls: ['./create-or-edit-site.component.scss'],
    standalone: false
})
export class CreateOrEditSiteComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    name: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    description: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    site_value: new FormControl(
      { value: null, disabled: false },
      { validators: [Validators.required, Validators.min(0)] }
    ),
    start_date: new FormControl({ value: null, disabled: false }),
    end_date: new FormControl({ value: null, disabled: false }),
    latitude: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
  });

  errorMessages: any = {};
  errorMessageMap = {
    name: {
      required: 'This field is required.',
    },
    description: {
      required: 'This field is required.',
    },
    site_value: {
      required: 'This field is required.',
    },
    start_date: {
      required: 'This field is required.',
    },
    end_date: {
      required: 'This field is required.',
    },
  };
  isFormDirty = false;
  projectId: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[segments.length - 1];
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data?.projectId;
    }

    if (this.data?.id || this.data?.uuid || this.data?.name) {
      this.form.patchValue({
        name: this.data?.name || null,
        description: this.data?.description || null,
        site_value: this.data?.site_value || null,
        start_date: this.data?.start_date ? moment(this.data.start_date) : null,
        end_date: this.data?.end_date ? moment(this.data.end_date) : null,
        latitude: this.data?.latitude || null,
        longitude: this.data?.longitude || null,
      });
    }

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue() as any;

    if (values.latitude == null || values.longitude == null) {
      this.commonService.notification('Location is required', 'error');
      return;
    }

    const startDate = values.start_date;
    const endDate = values.end_date;

    let duration = 0;
    if (startDate && endDate) {
      duration = endDate.diff(startDate, 'days') + 1;
    }

    const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

    const payload: any = {
      name: values.name,
      description: values.description,
      site_value: values.site_value,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      duration: duration,
      project_id: this.projectId,
      latitude: values.latitude || null,
      longitude: values.longitude || null,
    };

    const isEditMode = this.data?.id || this.data?.uuid;

    if (isEditMode) {
      payload.id = this.data?.id || this.data?.uuid;
      this.store.dispatch(
        UpdateSiteRequest({
          payload,
        })
      );
    } else {
      this.store.dispatch(
        CreateSiteRequest({
          payload,
        })
      );
    }
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField);
    if (currentField && !validationErrors[currentField]) {
      // Field is now valid — clear any stale error message left over from a prior submit
      const remaining = { ...this.errorMessages };
      delete remaining[currentField];
      this.errorMessages = remaining;
      return;
    }
    this.errorMessages = {
      ...this.errorMessages,
      ...validationErrors,
    };
  }

  showLocationSection(): boolean {
    return (
      this.form?.controls?.latitude?.value !== null &&
      this.form?.controls?.longitude?.value !== null &&
      this.form?.controls?.latitude?.value !== '' &&
      this.form?.controls?.longitude?.value !== ''
    );
  }

  editLocation(): void {
    let location: any = {
      latitude: this.form.value.latitude,
      longitude: this.form.value.longitude,
    };

    this.store
      .select(currentLocation)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(currentLoc => {
        if (!currentLoc?.latitude || !currentLoc?.longitude) {
          this.store
            .select(siteDetails)
            .pipe(takeUntil(this.onDestroy))
            .subscribe(site => {
              if (site?.longitude && site?.latitude) {
                location = {
                  latitude: site?.latitude,
                  longitude: site?.longitude,
                };
                this.openLocationPicker(location);
              } else {
                this.store
                  .select(projectDetails)
                  .pipe(takeUntil(this.onDestroy))
                  .subscribe(project => {
                    if (project?.longitude && project?.latitude) {
                      location = {
                        latitude: project?.latitude,
                        longitude: project?.longitude,
                      };
                    }
                    this.openLocationPicker(location);
                  });
              }
            });
        } else {
          location = {
            latitude: currentLoc?.latitude,
            longitude: currentLoc?.longitude,
          };
          this.openLocationPicker(location);
        }
      });
  }

  openLocationPicker(location: any): void {
    const dialogRef = this.dialog.open(LocationPickerComponent, {
      disableClose: true,
      data: {
        ...location,
        readOnly: false,
      },
    });

    dialogRef.afterClosed().subscribe(resp => {
      if (resp && 'latitude' in resp && 'longitude' in resp) {
        this.form.patchValue({
          latitude: resp.latitude,
          longitude: resp.longitude,
        });
      }
    });
  }

  deleteLocation(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.form.patchValue({
      latitude: null,
      longitude: null,
    });
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
